import { WebWorkerService } from './push-worker-service.ts'
import type { DomainConfig } from '@core/types/domain-config.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import {
    type PermissionRequestResponse,
    type PushRegistrationDelegate,
    AbstractPushRegistrationService,
} from './abstract-push-registration-service.ts'
import { Env } from '../../env.ts'
import { ensureError } from '@core/utils/ensure-error.ts'

/** @internal */
export class WebPushRegistrationService extends AbstractPushRegistrationService<PushSubscriptionJSON> {
    public constructor(
        private readonly workerService: WebWorkerService,
        delegate: PushRegistrationDelegate,
        domain: DomainConfig,
        logger?: Logger,
    ) {
        super(delegate, domain, logger ?? TaggedLogger._getInstance('WebPushRegistrationService'))
    }

    protected get _permissionsApi(): Permissions {
        return Env._navigator.permissions
    }

    protected get _notificationApi(): typeof window.Notification | undefined {
        return (Env._scope as typeof window)['Notification']
    }

    // region AbstractPushRegistrationService Overrides
    /**
     * Returns the {@link PushSubscriptionJSON} from the current subscription.
     */
    public async _getToken() {
        return (await this._getCurrentPermissions()).token
    }

    /**
     * Builds and returns a {@link PermissionRequestResponse} object
     * using the following values:
     *
     * **permission** - Attempts to pull current value using a
     * [permissions API query]{@link window.navigator.permissions.query} if able
     * and if not falls back to the legacy {@link window.Notification.permission} getter.
     *
     * **token** - If current permission is `granted` attempts to pull the
     * {@link PushSubscriptionJSON} from the active subscription.
     */
    public async _getCurrentPermissions() {
        let res: PermissionRequestResponse<PushSubscriptionJSON> = AbstractPushRegistrationService._defaultPermissions

        try {
            // Prioritize the new Permission API query method
            const perms = await this._permissionsApi.query({ name: 'notifications' })
            res.permission = perms.state === 'prompt' ? 'default' : perms.state
        } catch (_permsErr) {
            // Fallback to legacy Notification.permission getter
            if (this._notificationApi) {
                res.permission = this._notificationApi.permission
            }
        }

        if (res.permission === 'granted') {
            try {
                const sub = await this.workerService._getSubscription()
                if (sub) {
                    res.token = sub.toJSON()
                }
            } catch (subErr) {
                this.logger.warn('Unable to read current subscription.', ensureError(subErr).message)
            }
        }

        return res
    }

    /**
     * If current permissions return `default` a permissions request is made
     * via {@link _requestWebPushPermissions}. If, however, current permissions
     * return either `granted` or `denied` no request is made and the existing
     * values are returned.
     *
     * **Edge Case:**
     * When a service worker, or the current subscription, is removed
     * while browser permissions are still `granted` no subscription will
     * be available. In these scenarios the user should be sent through
     * the normal request flow which will result in a silent
     * re-subscription due to the permissions already being `granted`.
     */
    public async _requestNotificationPermission(
        currentPermissions?: PermissionRequestResponse<PushSubscriptionJSON>,
    ) {
        // Reuse the caller's already-resolved permissions when provided so the prompt
        // path performs a single navigator.permissions.query() rather than three
        // sequential reads (latency; and preserves transient activation for a
        // gesture-triggered prompt — NS-2558).
        currentPermissions ??= await this._getCurrentPermissions()

        if (
            currentPermissions.permission === 'denied' ||
            (currentPermissions.permission === 'granted' && currentPermissions.token)
        ) {
            this.logger.debug(`Permissions already ${currentPermissions.permission}`)
            return currentPermissions
        }

        // Edge-case: granted w/o subscription (token)
        if (currentPermissions.permission === 'granted') {
            this.logger.info('Subscription not found. Attempting to resubscribe.')
        }

        try {
            return this._requestWebPushPermissions(currentPermissions)
        } catch (err) {
            this.logger.debug('RNP', err)
            return this._getCurrentPermissions()
        }
    }
    // endregion

    // region Permissions
    /**
     * Requests permission via {@link window.Notification.requestPermission} and forwards
     * responses to their appropriate handlers.
     *
     * **Note:** {@link _onPushPermissionRequested} will only be invoked
     * if the browser permissions are `default`. All other {@link PermissionState}
     * values will not result in the browser dialog being presented to the user.
     */
    protected async _requestWebPushPermissions(
        currentPermissions?: PermissionRequestResponse<PushSubscriptionJSON>,
    ): Promise<PermissionRequestResponse<PushSubscriptionJSON>> {
        const res = currentPermissions ?? (await this._getCurrentPermissions())

        if (res.permission === 'default') {
            this.delegate?._onPushPermissionRequested?.()
            this.logger.info('Requesting permission')
        }

        res.permission = await this._notificationApi!.requestPermission()

        // Invoke any provided delegates
        switch (res.permission) {
            case 'granted':
                await this._handlePermissionGranted()
                break
            case 'denied':
                await this._handlePermissionDenied()
                break
            case 'default':
                await this._handlePermissionDismissed()
                break
        }

        return res
    }

    /**
     * When a `granted` response is returned a service worker subscription
     * attempt is performed and the following delegated methods are invoked:
     *
     * - Granted: {@link PushRegistrationDelegate#_onPushPermissionGranted}
     * - Subscription Success: {@link PushRegistrationDelegate#_onPushSubscriptionSucceeded}
     * - Subscription Failure: {@link PushRegistrationDelegate#_onPushSubscriptionFailed}
     */
    protected async _handlePermissionGranted() {
        this.logger.verbose('Handling permission granted status')

        this.delegate?._onPushPermissionGranted?.()

        const subRes = await this.workerService._subscribe()
        if (subRes.isOk() && subRes.unwrap()) {
            const pushSubscription = subRes.unwrap()
            if (pushSubscription) {
                this.delegate?._onPushSubscriptionSucceeded?.(pushSubscription.toJSON())
            } else {
                this.logger.warn('Subscription request returned null.')
            }
        } else {
            const error = subRes.getErr()!
            this.logger.error('Unable to perform subscription', error)
            this.delegate?._onPushSubscriptionFailed?.(error)
        }
    }

    /**
     * Forwards response through the following delegated method:
     * {@link PushRegistrationDelegate#_onPushPermissionDenied}.
     */
    protected async _handlePermissionDenied() {
        this.delegate?._onPushPermissionDenied?.()
    }

    /**
     * Forwards response through the following delegated method:
     * {@link PushRegistrationDelegate#_onPushPermissionDismissed}.
     */
    protected async _handlePermissionDismissed() {
        this.delegate?._onPushPermissionDismissed?.()
    }
    // endregion
}
